import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { calculateChart } from '../utils/astrology'
import { ChartType, ChartTypeNames } from '../types'
import { 
  SparklesIcon, 
  MapPinIcon, 
  CalendarIcon, 
  ClockIcon,
  ChevronRightIcon,
  InformationCircleIcon,
  UserIcon,
  UsersIcon,
  ArrowPathIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline'

interface PersonFormData {
  name: string
  birthDate: string
  birthTime: string
  birthLocation: string
  gender: 'male' | 'female' | ''
}

interface TransitFormData {
  transitDate: string
  transitTime: string
  transitLocation: string
}

export default function ChartGenerator() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const [loading, setLoading] = useState(false)
  const [step, setStep] = useState<'type' | 'form'>('type')
  const [selectedType, setSelectedType] = useState<ChartType>('natal')
  const [focusedField, setFocusedField] = useState<string | null>(null)
  
  // 第一人信息
  const [person1, setPerson1] = useState<PersonFormData>({
    name: '',
    birthDate: '',
    birthTime: '12:00',
    birthLocation: '',
    gender: ''
  })
  
  // 第二人信息（用于双人盘）
  const [person2, setPerson2] = useState<PersonFormData>({
    name: '',
    birthDate: '',
    birthTime: '12:00',
    birthLocation: '',
    gender: ''
  })
  
  // 推运信息
  const [transit, setTransit] = useState<TransitFormData>({
    transitDate: new Date().toISOString().split('T')[0],
    transitTime: new Date().toTimeString().slice(0, 5),
    transitLocation: ''
  })

  const chartTypes: ChartType[] = [
    'natal', 'transit', 'progressed', 'solar_arc', 'tertiary',
    'synastry', 'composite', 'davison'
  ]

  const isDoubleChart = ['synastry', 'composite', 'davison'].includes(selectedType)
  const isTransitChart = ['transit', 'progressed', 'solar_arc', 'tertiary'].includes(selectedType)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!person1.birthDate) return

    setLoading(true)

    try {
      // 计算第一个人的星盘
      const chartData1 = await calculateChart({
        birthDate: person1.birthDate,
        birthTime: person1.birthTime,
        birthLocation: person1.birthLocation,
      })

      let chartData2 = null
      
      // 如果需要第二人
      if (isDoubleChart && person2.birthDate) {
        chartData2 = await calculateChart({
          birthDate: person2.birthDate,
          birthTime: person2.birthTime,
          birthLocation: person2.birthLocation,
        })
      }
      
      // 如果是推运盘，计算推运数据
      if (isTransitChart && transit.transitDate) {
        chartData2 = await calculateChart({
          birthDate: transit.transitDate,
          birthTime: transit.transitTime,
          birthLocation: transit.transitLocation || person1.birthLocation,
        })
      }

      const chartId = `temp_${Date.now()}`
      const chartData = {
        id: chartId,
        chart_type: selectedType,
        name: getChartName(),
        birth_date: person1.birthDate,
        birth_time: person1.birthTime,
        birth_location: person1.birthLocation,
        gender: person1.gender || undefined,
        person2_name: isDoubleChart ? person2.name : undefined,
        person2_birth_date: isDoubleChart ? person2.birthDate : undefined,
        person2_birth_time: isDoubleChart ? person2.birthTime : undefined,
        person2_birth_location: isDoubleChart ? person2.birthLocation : undefined,
        person2_gender: isDoubleChart ? person2.gender || undefined : undefined,
        transit_date: isTransitChart ? transit.transitDate : undefined,
        transit_time: isTransitChart ? transit.transitTime : undefined,
        transit_location: isTransitChart ? transit.transitLocation : undefined,
        chart_data: chartData1,
        chart_data2: chartData2,
        createdAt: new Date().toISOString(),
      }

      localStorage.setItem(`chart_${chartId}`, JSON.stringify(chartData))
      navigate(`/chart/${chartId}`)
    } catch (error) {
      console.error('计算星盘失败:', error)
      alert('计算星盘失败，请检查输入信息')
    } finally {
      setLoading(false)
    }
  }

  const getChartName = () => {
    if (person1.name) return person1.name
    const typeName = ChartTypeNames[selectedType].name
    if (isDoubleChart) return `${typeName} - 双人分析`
    return `我的${typeName}`
  }

  // 渲染输入字段
  const renderInputField = (
    id: string,
    label: string,
    icon: any,
    type: string,
    value: string,
    onChange: (val: string) => void,
    placeholder: string,
    required: boolean,
    hint: string
  ) => {
    const Icon = icon
    const isFocused = focusedField === id
    
    return (
      <div className="relative">
        <label className="cyber-label flex items-center gap-2">
          <Icon className="w-4 h-4" />
          {label}
          {required && <span className="text-cyber-pink">*</span>}
        </label>
        
        <div className="relative group">
          <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => setFocusedField(id)}
            onBlur={() => setFocusedField(null)}
            placeholder={placeholder}
            required={required}
            className={`cyber-input pl-10 ${isFocused || value ? 'border-cyber-blue' : ''}`}
          />
          <Icon className="w-5 h-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          
          {(isFocused || value) && (
            <div className="absolute inset-0 rounded-lg pointer-events-none animate-pulse-slow"
              style={{
                boxShadow: '0 0 15px rgba(0, 240, 255, 0.2), inset 0 0 10px rgba(0, 240, 255, 0.05)'
              }}
            />
          )}
        </div>
        
        <p className="text-xs text-gray-500 mt-1.5 flex items-center gap-1">
          <InformationCircleIcon className="w-3 h-3" />
          {hint}
        </p>
      </div>
    )
  }

  // 选择星盘类型页面
  if (step === 'type') {
    return (
      <div className="min-h-screen pt-24 md:pt-32 pb-16 px-4 relative">
        {/* 背景装饰 */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-cyber-blue/5 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-0 w-[500px] h-[500px] bg-cyber-purple/5 rounded-full blur-3xl" />
        </div>

        <div className="max-w-5xl mx-auto relative">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyber-blue/10 border border-cyber-blue/20 mb-6">
              <SparklesIcon className="w-4 h-4 text-cyber-blue" />
              <span className="text-xs text-cyber-blue font-mono tracking-wider">CHART TYPE</span>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold mb-4 font-cyber">
              <span className="text-gradient">选择星盘类型</span>
            </h1>
            
            <p className="text-gray-400 max-w-md mx-auto">
              选择你想要生成的星盘类型，每种类型提供不同的占星视角
            </p>
          </div>

          {/* 分类标签 */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            <span className="px-3 py-1 text-xs rounded-full bg-cyber-blue/10 text-cyber-blue border border-cyber-blue/20">
              本命分析
            </span>
            <span className="px-3 py-1 text-xs rounded-full bg-cyber-purple/10 text-cyber-purple border border-cyber-purple/20">
              推运预测
            </span>
            <span className="px-3 py-1 text-xs rounded-full bg-cyber-pink/10 text-cyber-pink border border-cyber-pink/20">
              双人合盘
            </span>
          </div>

          {/* 星盘类型网格 */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {chartTypes.map((type) => {
              const info = ChartTypeNames[type]
              const isSelected = selectedType === type
              
              return (
                <button
                  key={type}
                  onClick={() => setSelectedType(type)}
                  className={`relative p-6 rounded-xl border text-left transition-all duration-300 ${
                    isSelected
                      ? 'bg-cyber-blue/10 border-cyber-blue/50 shadow-[0_0_30px_rgba(0,240,255,0.2)]'
                      : 'bg-cyber-dark/50 border-cyber-blue/10 hover:border-cyber-blue/30'
                  }`}
                >
                  {/* 选中标记 */}
                  {isSelected && (
                    <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-cyber-blue flex items-center justify-center">
                      <svg className="w-4 h-4 text-cyber-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                  
                  <div className="text-3xl mb-3">{info.icon}</div>
                  <h3 className="text-lg font-semibold text-white mb-1 font-cyber">{info.name}</h3>
                  <p className="text-xs text-gray-400">{info.desc}</p>
                  
                  {/* 分类标签 */}
                  <div className="mt-3">
                    {['synastry', 'composite', 'davison'].includes(type) ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyber-pink/20 text-cyber-pink">
                        双人
                      </span>
                    ) : ['transit', 'progressed', 'solar_arc', 'tertiary'].includes(type) ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyber-purple/20 text-cyber-purple">
                        推运
                      </span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyber-blue/20 text-cyber-blue">
                        本命
                      </span>
                    )}
                  </div>
                </button>
              )
            })}
          </div>

          {/* 下一步按钮 */}
          <div className="mt-10 text-center">
            <button
              onClick={() => setStep('form')}
              className="cyber-button text-lg inline-flex items-center gap-2"
            >
              <span>下一步</span>
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    )
  }

  // 表单页面
  return (
    <div className="min-h-screen pt-24 md:pt-32 pb-16 px-4 relative">
      {/* 背景装饰 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-cyber-blue/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-[500px] h-[500px] bg-cyber-purple/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-4xl mx-auto relative">
        {/* Header */}
        <div className="text-center mb-10">
          <button
            onClick={() => setStep('type')}
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-cyber-blue transition-colors mb-4"
          >
            <ArrowPathIcon className="w-4 h-4" />
            返回选择类型
          </button>
          
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyber-blue/10 border border-cyber-blue/20 mb-6">
            <span className="text-2xl">{ChartTypeNames[selectedType].icon}</span>
            <span className="text-xs text-cyber-blue font-mono tracking-wider uppercase">
              {ChartTypeNames[selectedType].name}
            </span>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold mb-4 font-cyber">
            <span className="text-gradient">填写出生信息</span>
          </h1>
          
          <p className="text-gray-400 max-w-md mx-auto">
            {ChartTypeNames[selectedType].desc}
          </p>
        </div>

        {/* Form Card */}
        <div className="cyber-card p-1">
          <div className="bg-cyber-dark/50 rounded-xl p-6 md:p-8">
            {/* 装饰角标 */}
            <div className="absolute top-0 left-0 w-8 h-8 border-l-2 border-t-2 border-cyber-blue rounded-tl-xl" />
            <div className="absolute top-0 right-0 w-8 h-8 border-r-2 border-t-2 border-cyber-blue rounded-tr-xl" />
            <div className="absolute bottom-0 left-0 w-8 h-8 border-l-2 border-b-2 border-cyber-blue rounded-bl-xl" />
            <div className="absolute bottom-0 right-0 w-8 h-8 border-r-2 border-b-2 border-cyber-blue rounded-br-xl" />

            <form onSubmit={handleSubmit} className="space-y-8 relative">
              {/* 第一人信息 */}
              <div className="space-y-6">
                <div className="flex items-center gap-3 pb-4 border-b border-cyber-blue/20">
                  <div className="w-10 h-10 rounded-lg bg-cyber-blue/20 flex items-center justify-center">
                    <UserIcon className="w-5 h-5 text-cyber-blue" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white font-cyber">
                      {isDoubleChart ? '第一主角' : '个人信息'}
                    </h3>
                    <p className="text-xs text-gray-500">
                      {isDoubleChart ? '输入第一位当事人的出生信息' : '输入你的出生信息'}
                    </p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {renderInputField(
                    'p1-name',
                    '姓名（可选）',
                    UserIcon,
                    'text',
                    person1.name,
                    (v) => setPerson1({...person1, name: v}),
                    '如：张三',
                    false,
                    '用于标识这个星盘'
                  )}
                  
                  <div className="relative">
                    <label className="cyber-label flex items-center gap-2">
                      <UserIcon className="w-4 h-4" />
                      性别
                    </label>
                    <select
                      value={person1.gender}
                      onChange={(e) => setPerson1({...person1, gender: e.target.value as 'male' | 'female'})}
                      className="cyber-input appearance-none cursor-pointer"
                    >
                      <option value="">请选择</option>
                      <option value="male">男</option>
                      <option value="female">女</option>
                    </select>
                  </div>

                  {renderInputField(
                    'p1-date',
                    '出生日期 *',
                    CalendarIcon,
                    'date',
                    person1.birthDate,
                    (v) => setPerson1({...person1, birthDate: v}),
                    '',
                    true,
                    '选择出生日期'
                  )}

                  {renderInputField(
                    'p1-time',
                    '出生时间 *',
                    ClockIcon,
                    'time',
                    person1.birthTime,
                    (v) => setPerson1({...person1, birthTime: v}),
                    '',
                    true,
                    '不确定可使用 12:00'
                  )}

                  <div className="md:col-span-2">
                    {renderInputField(
                      'p1-location',
                      '出生地点 *',
                      MapPinIcon,
                      'text',
                      person1.birthLocation,
                      (v) => setPerson1({...person1, birthLocation: v}),
                      '如：北京市朝阳区',
                      true,
                      '精确到城市即可'
                    )}
                  </div>
                </div>
              </div>

              {/* 第二人信息（双人盘） */}
              {isDoubleChart && (
                <div className="space-y-6 pt-6 border-t border-cyber-blue/10">
                  <div className="flex items-center gap-3 pb-4 border-b border-cyber-pink/20">
                    <div className="w-10 h-10 rounded-lg bg-cyber-pink/20 flex items-center justify-center">
                      <UsersIcon className="w-5 h-5 text-cyber-pink" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white font-cyber">第二主角</h3>
                      <p className="text-xs text-gray-500">输入第二位当事人的出生信息</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {renderInputField(
                      'p2-name',
                      '姓名（可选）',
                      UserIcon,
                      'text',
                      person2.name,
                      (v) => setPerson2({...person2, name: v}),
                      '如：李四',
                      false,
                      '用于标识'
                    )}
                    
                    <div className="relative">
                      <label className="cyber-label flex items-center gap-2">
                        <UserIcon className="w-4 h-4" />
                        性别
                      </label>
                      <select
                        value={person2.gender}
                        onChange={(e) => setPerson2({...person2, gender: e.target.value as 'male' | 'female'})}
                        className="cyber-input appearance-none cursor-pointer"
                      >
                        <option value="">请选择</option>
                        <option value="male">男</option>
                        <option value="female">女</option>
                      </select>
                    </div>

                    {renderInputField(
                      'p2-date',
                      '出生日期 *',
                      CalendarIcon,
                      'date',
                      person2.birthDate,
                      (v) => setPerson2({...person2, birthDate: v}),
                      '',
                      true,
                      '选择出生日期'
                    )}

                    {renderInputField(
                      'p2-time',
                      '出生时间 *',
                      ClockIcon,
                      'time',
                      person2.birthTime,
                      (v) => setPerson2({...person2, birthTime: v}),
                      '',
                      true,
                      '不确定可使用 12:00'
                    )}

                    <div className="md:col-span-2">
                      {renderInputField(
                        'p2-location',
                        '出生地点 *',
                        MapPinIcon,
                        'text',
                        person2.birthLocation,
                        (v) => setPerson2({...person2, birthLocation: v}),
                        '如：上海市浦东新区',
                        true,
                        '精确到城市即可'
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* 推运时间（推运盘） */}
              {isTransitChart && (
                <div className="space-y-6 pt-6 border-t border-cyber-purple/10">
                  <div className="flex items-center gap-3 pb-4 border-b border-cyber-purple/20">
                    <div className="w-10 h-10 rounded-lg bg-cyber-purple/20 flex items-center justify-center">
                      <GlobeAltIcon className="w-5 h-5 text-cyber-purple" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white font-cyber">推运时间</h3>
                      <p className="text-xs text-gray-500">选择要推运到的目标时间</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {renderInputField(
                      'transit-date',
                      '推运日期 *',
                      CalendarIcon,
                      'date',
                      transit.transitDate,
                      (v) => setTransit({...transit, transitDate: v}),
                      '',
                      true,
                      '选择目标日期，默认今天'
                    )}

                    {renderInputField(
                      'transit-time',
                      '推运时间',
                      ClockIcon,
                      'time',
                      transit.transitTime,
                      (v) => setTransit({...transit, transitTime: v}),
                      '',
                      false,
                      '可选，默认当前时间'
                    )}

                    <div className="md:col-span-2">
                      {renderInputField(
                        'transit-location',
                        '推运地点',
                        MapPinIcon,
                        'text',
                        transit.transitLocation,
                        (v) => setTransit({...transit, transitLocation: v}),
                        '如：北京市（默认使用出生地点）',
                        false,
                        '当前所在位置，默认使用出生地点'
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="pt-6">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full cyber-button flex items-center justify-center gap-2 disabled:opacity-50 text-lg"
                >
                  {loading ? (
                    <>
                      <div className="cyber-loader w-5 h-5" />
                      <span>星盘计算中...</span>
                    </>
                  ) : (
                    <>
                      <SparklesIcon className="w-5 h-5" />
                      <span>生成{ChartTypeNames[selectedType].name}</span>
                      <ChevronRightIcon className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>

              {/* Note */}
              {!isAuthenticated && (
                <div className="p-4 rounded-lg bg-cyber-blue/5 border border-cyber-blue/20">
                  <p className="text-sm text-gray-400 text-center">
                    <span className="text-cyber-yellow">⚠</span> 未登录状态下生成的星盘将在 24 小时后自动删除
                    <br />
                    <button 
                      type="button"
                      onClick={() => {
                        const authUrl = `https://auth.7e.ink/oauth/authorize?client_id=astro&redirect_uri=${encodeURIComponent(window.location.origin + '/auth/callback')}&response_type=code`
                        window.location.href = authUrl
                      }}
                      className="text-cyber-blue hover:underline mt-1 inline-block"
                    >
                      登录后可永久保存 →
                    </button>
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
